import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import classNames from "classnames/bind";
import { AnimatePresence } from "motion/react";
import { RhLink } from "@components";
import { easeOutQuad } from "@/core/easing";
import MenuIcon from "../MenuIcon/MenuIcon";
import SearchIcon from "@/assets/svg/search.svg";
import MobileMenu from "../MobileMenu/MobileMenu";
import TicketsIcon from "@/assets/svg/tickets.svg";
import { GetPageWrapperDataQuery } from "@graphqlTypes";
import { useHeader } from "@/providers/HeaderContext";
import { useViewSize } from "@/providers/ViewSizeContext";
import TabletSideMenu from "./TabletSideMenu/TabletSideMenu";
import { getLinkData, updateBodyScroll } from "@/core/utils";
import { useSearch } from "@/providers/SearchContext";

import styles from "./MobileSideNav.module.scss";
const cx = classNames.bind(styles);

type SideNavProps =
  SiteNavigation["navigationSettingsFields"] & {
    onSearchClick: () => void;
    languages: GetPageWrapperDataQuery["languages"];
  };

export default function MobileSideNav(props: SideNavProps) {
  const { ticketsCta, onSearchClick, languages } = props;
  const router = useRouter();
  const rootEl = useRef<HTMLDivElement>(null);

  const { showSearchTray, isSearchPage } = useSearch();
  const { setMenuExpanded, menuExpanded } = useHeader();
  const { viewWidth, isTablet, isMobile } = useViewSize();

  const ticketsLinkData = getLinkData(ticketsCta);
  const handleMenuToggle = () => {
    if (showSearchTray) { // hide search tray if visible
      onSearchClick();
    }
    setMenuExpanded(!menuExpanded);
  }
  const handleRouteChange = () => setMenuExpanded(false);

  useEffect(() => {
    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, []);
  useEffect(() => {
    if (viewWidth) setMenuExpanded(false);
  }, [viewWidth]);
  useEffect(() => {
    const checkClose = (event: MouseEvent) => {
      if (rootEl.current && !rootEl.current.contains(event.target as Node)) {
        event.stopPropagation();
        event.preventDefault();
        setMenuExpanded(false);
      }
    };
    updateBodyScroll(menuExpanded);
    if (menuExpanded) window.addEventListener("click", checkClose);
    return () => window.removeEventListener("click", checkClose);
  }, [menuExpanded]);
  return (
    <div className={cx("mobile-side-nav")} ref={rootEl}>
      {ticketsLinkData && (
        <RhLink
          className={cx(["icon-link", "tickets"])}
          href={ticketsLinkData.url}
          target={ticketsLinkData.target}
          aria-label={ticketsLinkData.label}
          data-gtm-section="nav"
        >
          <TicketsIcon className={cx("icon")} />
        </RhLink>
      )}
      <button
        className={cx(["icon-link", "search", { active: showSearchTray }])}
        disabled={isSearchPage}
        aria-label="Search"
        onClick={() => {
          setMenuExpanded(false);
          onSearchClick();
        }}
      >
        <SearchIcon className={cx("icon")} aria-hidden="true" />
      </button>
      <MenuIcon active={menuExpanded} onClick={handleMenuToggle} />
      {isTablet && (
        <AnimatePresence>
          {menuExpanded && (
            <TabletSideMenu
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: easeOutQuad }}
              {...props}
            />
          )}
        </AnimatePresence>
      )}
      {isMobile && (
        <AnimatePresence>
          {menuExpanded && (
            <MobileMenu
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.3, ease: easeOutQuad }}
              {...props}
            />
          )}
        </AnimatePresence>
      )}
    </div>
  );
}
